import { useState } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, Label,
} from "recharts";
import { Plus, Trash2 } from "lucide-react";
import { evalRef, cellDisplay, idxToCol, colToIdx, rangeGrid, expandRange, shiftFormulaRows, formatValue } from "@/lib/sheetEngine";
import SheetToolbar from "@/components/studio/SheetToolbar";

const PIE_COLORS = ["#22D3EE", "#F59E0B", "#34D399", "#818CF8", "#FB7185", "#A78BFA", "#38BDF8", "#FBBF24"];
const CF_FILL = { green: "#DCFCE7", red: "#FEE2E2", yellow: "#FEF3C7" };
const numOf = (v) => { const n = Number(String(v ?? "").trim()); return Number.isNaN(n) ? null : n; };

export default function SheetEditorCore({ doc, setDoc, config, pageRef }) {
  const active = doc.activeSheet || 0;
  const sheet = doc.sheets[active];
  const cells = sheet.cells || {};
  const fmt = sheet.fmt || {};
  const [sel, setSel] = useState("A1");
  const [focused, setFocused] = useState(null);
  const [open, setOpen] = useState(null);
  const [fill, setFill] = useState(null); // {from, col, row, toRow} drag state for the fill handle

  const updateSheet = (patch) => setDoc({ ...doc, sheets: doc.sheets.map((s, i) => (i === active ? { ...s, ...patch } : s)) });
  const setCell = (ref, val) => updateSheet({ cells: { ...cells, [ref]: val } });
  const setFmtFor = (ref, patch) => updateSheet({ fmt: { ...fmt, [ref]: { ...(fmt[ref] || {}), ...patch } } });
  const fmtOf = (ref) => fmt[ref];

  const selCol = sel.replace(/\d+/, "");
  const selRow = parseInt(sel.replace(/\D+/g, ""), 10) || 1;
  const selRaw = cells[sel] || "";

  const shiftCells = (predicate, move) => {
    const next = {};
    Object.entries(cells).forEach(([ref, v]) => {
      const c = colToIdx(ref.replace(/\d+/g, "")), r = parseInt(ref.replace(/\D+/g, ""), 10);
      if (predicate(c, r) === "drop") return;
      const [nc, nr] = move(c, r);
      next[`${idxToCol(nc)}${nr}`] = v;
    });
    return next;
  };

  const actions = {
    sort: (order) => {
      // Sorts the whole table like Sheets/Excel: whole rows move, the header row stays put.
      const colIdx = colToIdx(selCol);
      const at = (c, r) => (cells[`${idxToCol(c)}${r}`] || "").trim();
      const colUsed = (c) => { for (let r = 1; r <= sheet.rows; r++) if (at(c, r) !== "") return true; return false; };
      let c0 = colIdx, c1 = colIdx;
      while (c0 > 0 && colUsed(c0 - 1)) c0--;
      while (c1 < sheet.cols - 1 && colUsed(c1 + 1)) c1++;

      const runs = [];
      for (let r = 1; r <= sheet.rows; r++) {
        if (at(colIdx, r) === "") continue;
        if (runs.length && runs[runs.length - 1].at(-1) === r - 1) runs[runs.length - 1].push(r);
        else runs.push([r]);
      }
      let run = runs.find((g) => g.includes(selRow)) || runs[0];
      if (!run) return;
      if (sheet.freeze) run = run.filter((r) => r > sheet.freeze);
      // Trim label/total rows at the edges: a row whose sort cell is a formula (a total row) or that is
      // only partly filled (a stray "Goal: 150" line above the table) is not part of the data.
      const filled = (r) => { let n = 0; for (let c = c0; c <= c1; c++) if (at(c, r) !== "") n++; return n; };
      const full = Math.max(...run.map(filled));
      const edgeNoise = (r) => at(colIdx, r).startsWith("=") || filled(r) < full;
      while (run.length && edgeNoise(run[run.length - 1])) run = run.slice(0, -1);
      while (run.length && edgeNoise(run[0])) run = run.slice(1);
      // ...and the header row stays on top: text above a column of numbers
      if (run.length > 2 && numOf(at(colIdx, run[0])) === null && run.slice(1).some((r) => numOf(at(colIdx, r)) !== null)) run = run.slice(1);
      if (run.length < 2) return;

      const keyOf = (r) => {
        const raw = at(colIdx, r);
        return raw.startsWith("=") ? String(evalRef(cells, `${selCol}${r}`) ?? "") : raw;
      };
      const rows = run.map((r) => {
        const vals = {};
        for (let c = c0; c <= c1; c++) vals[c] = cells[`${idxToCol(c)}${r}`] ?? "";
        const key = keyOf(r);
        return { row: r, key, num: numOf(key), vals };
      });
      const dir = order === "asc" ? 1 : -1;
      rows.sort((a, b) => dir * (a.num !== null && b.num !== null ? a.num - b.num : a.key.localeCompare(b.key, undefined, { numeric: true, sensitivity: "base" })));
      const next = { ...cells };
      run.forEach((r, i) => {
        const from = rows[i];
        for (let c = c0; c <= c1; c++) {
          const ref = `${idxToCol(c)}${r}`;
          const v = from.vals[c];
          if (v === "" || v === undefined) delete next[ref];
          else next[ref] = String(v).startsWith("=") ? shiftFormulaRows(String(v), r - from.row) : v;
        }
      });
      updateSheet({ cells: next });
    },
    merge: () => {
      const range = window.prompt("Merge which cells? (e.g. A1:C1)", `${selCol}${selRow}:${idxToCol(colToIdx(selCol) + 2)}${selRow}`);
      if (!range || !range.includes(":")) return;
      updateSheet({ merges: [...(sheet.merges || []), range.toUpperCase()] });
    },
    insertRow: () => updateSheet({ rows: sheet.rows + 1, cells: shiftCells(() => null, (c, r) => [c, r >= selRow ? r + 1 : r]) }),
    deleteRow: () => updateSheet({ rows: Math.max(2, sheet.rows - 1), cells: shiftCells((c, r) => (r === selRow ? "drop" : null), (c, r) => [c, r > selRow ? r - 1 : r]) }),
    insertCol: () => updateSheet({ cols: sheet.cols + 1, cells: shiftCells(() => null, (c, r) => [c >= colToIdx(selCol) ? c + 1 : c, r]) }),
    deleteCol: () => updateSheet({ cols: Math.max(2, sheet.cols - 1), cells: shiftCells((c) => (c === colToIdx(selCol) ? "drop" : null), (c, r) => [c > colToIdx(selCol) ? c - 1 : c, r]) }),
    widenCol: () => updateSheet({ colWidths: { ...(sheet.colWidths || {}), [selCol]: Math.min(320, ((sheet.colWidths || {})[selCol] || 90) + 50) } }),
    freeze: () => updateSheet({ freeze: sheet.freeze ? 0 : 1 }),
    filter: (f) => updateSheet({ filter: f && f.range ? { range: f.range.toUpperCase(), col: (f.col || "").toUpperCase(), value: f.value } : null }),
    condFmt: (rule) => {
      if (!rule.range.includes(":")) return;
      updateSheet({ condFmt: [...(sheet.condFmt || []), { ...rule, range: rule.range.toUpperCase(), value: rule.value }] });
    },
    addChart: (c) => {
      if (!c.range.includes(":")) return false;
      setDoc({ ...doc, charts: [...(doc.charts || []), { ...c, id: `ch_${Date.now()}`, range: c.range.toUpperCase(), sheet: active }] });
      return true;
    },
  };

  const removeChart = (id) => setDoc({ ...doc, charts: (doc.charts || []).filter((c) => c.id !== id) });
  const removeRule = (i) => updateSheet({ condFmt: (sheet.condFmt || []).filter((_, idx) => idx !== i) });

  const addSheet = () => setDoc({ ...doc, sheets: [...doc.sheets, { name: `Sheet${doc.sheets.length + 1}`, rows: 8, cols: 4, cells: {} }], activeSheet: doc.sheets.length });
  const renameSheet = (i) => {
    const name = window.prompt("Rename worksheet", doc.sheets[i].name);
    if (name) setDoc({ ...doc, sheets: doc.sheets.map((s, idx) => (idx === i ? { ...s, name } : s)) });
  };

  // ---- fill handle: drag the little square down to copy a formula, rows shifting as they do in Sheets/Excel
  const commitFill = () => {
    if (!fill || fill.toRow <= fill.row) { setFill(null); return; }
    const src = cells[`${fill.col}${fill.row}`] || "";
    if (!src) { setFill(null); return; }
    const next = { ...cells };
    for (let r = fill.row + 1; r <= fill.toRow; r++) {
      next[`${fill.col}${r}`] = src.startsWith("=") ? shiftFormulaRows(src, r - fill.row) : src;
    }
    updateSheet({ cells: next });
    setFill(null);
  };

  const hiddenRows = (() => {
    const f = sheet.filter;
    if (!f || !f.range || !f.col || f.value == null || f.value === "") return new Set();
    const refs = expandRange(f.range);
    if (!refs.length) return new Set();
    const rows = refs.map((r) => parseInt(r.replace(/\D+/g, ""), 10));
    const first = Math.min(...rows), last = Math.max(...rows);
    const hide = new Set();
    for (let r = first + 1; r <= last; r++) {
      const v = String(evalRef(cells, `${f.col}${r}`) ?? "").trim().toLowerCase();
      if (v !== String(f.value).trim().toLowerCase()) hide.add(r);
    }
    return hide;
  })();

  // ---- Enter commits the cell and drops to the next row (Shift+Enter goes up), as in Sheets/Excel
  const onCellKeyDown = (e, col, row) => {
    if (e.key === "Escape") { e.currentTarget.blur(); return; }
    if (e.key !== "Enter") return;
    e.preventDefault();
    const step = e.shiftKey ? -1 : 1;
    let r = row + step;
    while (r >= 1 && r <= sheet.rows && hiddenRows.has(r)) r += step;
    const next = r >= 1 && r <= sheet.rows ? document.querySelector(`[data-testid="sheet-cell-${col}${r}"]`) : null;
    if (next) { setSel(`${col}${r}`); next.focus(); next.select(); } else e.currentTarget.blur();
  };

  const mergedInfo = (ref) => {
    for (const m of sheet.merges || []) {
      const refs = expandRange(m);
      if (refs[0] === ref) return { span: refs.length, head: true };
      if (refs.includes(ref)) return { span: 0, head: false };
    }
    return null;
  };

  const cfStyle = (ref) => {
    for (const rule of sheet.condFmt || []) {
      if (!expandRange(rule.range).includes(ref)) continue;
      const v = numOf(evalRef(cells, ref));
      const t = numOf(rule.value);
      if (v === null || t === null) continue;
      const hit = rule.op === ">" ? v > t : rule.op === "<" ? v < t : rule.op === ">=" ? v >= t : rule.op === "<=" ? v <= t : v === t;
      if (hit) return CF_FILL[rule.style] || CF_FILL.green;
    }
    return null;
  };

  const chartData = (range) => {
    const grid = rangeGrid(range);
    if (!grid) return [];
    return grid.map((row) => {
      const name = cellDisplay(cells, row[0]) || row[0];
      const v = evalRef(cells, row.length > 1 ? row[1] : row[0]);
      return { name, value: typeof v === "number" ? v : Number(v) || 0 };
    });
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10" onMouseUp={commitFill}>
      <SheetToolbar sel={sel} selRaw={selRaw} setCell={setCell} config={config} sheet={sheet}
        fmtOf={fmtOf} setFmt={setFmtFor} actions={actions} open={open} setOpen={setOpen} />

      <div className="bg-slate-200 p-3 sm:p-5 max-h-[560px] overflow-auto hq-scrollbar" onClick={() => setOpen(null)}>
        <div ref={pageRef} className="bg-white rounded-sm p-3 sm:p-4 inline-block min-w-full">
          <table className="border-collapse">
            <thead>
              <tr>
                <th className="w-10 h-7 bg-slate-100 border border-slate-300 sticky left-0" />
                {Array.from({ length: sheet.cols }).map((_, c) => (
                  <th key={c} data-testid={`sheet-col-${idxToCol(c)}`} style={{ minWidth: (sheet.colWidths || {})[idxToCol(c)] || 90 }}
                    className="h-7 bg-slate-100 border border-slate-300 text-xs font-mono-data text-slate-600">{idxToCol(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: sheet.rows }).map((_, r) => {
                const row = r + 1;
                if (hiddenRows.has(row)) return null;
                const frozen = !!sheet.freeze && row <= sheet.freeze;
                return (
                  <tr key={r} data-testid={`sheet-row-${row}`} className={frozen ? "sticky top-7 z-10" : ""}>
                    <td className={`w-10 h-8 border border-slate-300 text-center text-xs font-mono-data text-slate-600 ${frozen ? "bg-cyan-50" : "bg-slate-100"}`}>{row}</td>
                    {Array.from({ length: sheet.cols }).map((_, c) => {
                      const ref = `${idxToCol(c)}${row}`;
                      const mi = mergedInfo(ref);
                      if (mi && !mi.head) return null;
                      const isSel = ref === sel;
                      const raw = cells[ref] || "";
                      const cf = fmt[ref] || {};
                      const display = focused === ref ? raw : cellDisplay(cells, ref, cf.numFmt);
                      const isNum = raw !== "" && !raw.startsWith("=") && numOf(raw) !== null;
                      const isFormula = raw.startsWith("=");
                      const bg = cfStyle(ref) || cf.fill || (frozen ? "#F8FAFC" : undefined);
                      const inFillRange = fill && fill.col === idxToCol(c) && row > fill.row && row <= fill.toRow;
                      return (
                        <td key={c} colSpan={mi ? mi.span : 1}
                          onMouseEnter={() => fill && setFill({ ...fill, toRow: fill.col === idxToCol(c) ? row : fill.toRow })}
                          className={`border p-0 relative ${cf.border ? "border-slate-500 border-2" : "border-slate-300"} ${isSel ? "ring-2 ring-[#22D3EE] ring-inset" : ""} ${inFillRange ? "ring-1 ring-[#22D3EE]/60 ring-inset" : ""}`}
                          style={{ background: bg }}>
                          <input
                            data-testid={`sheet-cell-${ref}`}
                            value={display}
                            onFocus={() => { setSel(ref); setFocused(ref); }}
                            onBlur={() => setFocused(null)}
                            onKeyDown={(e) => onCellKeyDown(e, idxToCol(c), row)}
                            onChange={(e) => setCell(ref, e.target.value)}
                            style={{ textAlign: cf.align || (isNum || isFormula ? "right" : "left"), whiteSpace: cf.wrap ? "normal" : "nowrap" }}
                            className={`w-full h-8 px-1.5 text-sm outline-none bg-transparent ${cf.bold ? "font-bold" : ""} ${isFormula && focused !== ref ? "text-emerald-700 font-medium" : "text-slate-800"}`}
                          />
                          {isSel && (
                            <span data-testid={`sheet-fill-handle-${ref}`} title="Drag down to fill this formula"
                              onMouseDown={(e) => { e.preventDefault(); setFill({ col: idxToCol(c), row, toRow: row }); }}
                              className="absolute -bottom-[3px] -right-[3px] w-2 h-2 bg-[#0E7490] cursor-crosshair rounded-[1px]" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {(sheet.condFmt || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="sheet-cf-rules">
              {(sheet.condFmt || []).map((rule, i) => (
                <span key={i} className="text-[11px] px-2 py-1 rounded-full border border-slate-300 text-slate-600 flex items-center gap-1.5" style={{ background: CF_FILL[rule.style] }}>
                  {rule.range} {rule.op} {rule.value}
                  <button data-testid={`sheet-cf-remove-${i}`} onClick={() => removeRule(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          {sheet.filter && (
            <p data-testid="sheet-filter-badge" className="mt-3 text-[11px] text-slate-500 font-mono-data">
              Filter on {sheet.filter.range} · column {sheet.filter.col} = {String(sheet.filter.value)}
            </p>
          )}

          {(doc.charts || []).length > 0 && (
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              {(doc.charts || []).map((c) => {
                const data = chartData(c.range);
                const axis = (t, pos) => (t ? <Label value={t} position={pos} style={{ fontSize: 11, fill: "#475569" }} /> : null);
                return (
                  <div key={c.id} data-testid={`sheet-chart-${c.type}`} className="border border-slate-200 rounded-lg p-3 relative">
                    <button data-testid={`sheet-chart-remove-${c.id}`} onClick={() => removeChart(c.id)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500 z-10"><Trash2 className="w-3.5 h-3.5" /></button>
                    {c.title
                      ? <p data-testid={`sheet-chart-title-${c.type}`} className="text-sm text-slate-800 font-medium text-center mb-1">{c.title}</p>
                      : <p className="text-xs text-slate-400 italic text-center mb-1">Untitled chart</p>}
                    <p className="text-[10px] text-slate-400 mb-2 font-mono-data text-center">{c.range}</p>
                    <ResponsiveContainer width="100%" height={210}>
                      {c.type === "pie" ? (
                        <PieChart>
                          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={{ fontSize: 10 }}>
                            {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          {c.legend !== false && <Legend wrapperStyle={{ fontSize: 11 }} />}
                          <Tooltip />
                        </PieChart>
                      ) : c.type === "line" ? (
                        <LineChart data={data} margin={{ bottom: c.xTitle ? 18 : 0, left: c.yTitle ? 10 : 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }}>{axis(c.xTitle, "insideBottom")}</XAxis>
                          <YAxis tick={{ fontSize: 11, fill: "#475569" }}>{axis(c.yTitle, "insideLeft")}</YAxis>
                          <Tooltip />
                          {c.legend !== false && <Legend wrapperStyle={{ fontSize: 11 }} />}
                          <Line type="monotone" dataKey="value" stroke="#0E7490" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      ) : (
                        <BarChart data={data} margin={{ bottom: c.xTitle ? 18 : 0, left: c.yTitle ? 10 : 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }}>{axis(c.xTitle, "insideBottom")}</XAxis>
                          <YAxis tick={{ fontSize: 11, fill: "#475569" }}>{axis(c.yTitle, "insideLeft")}</YAxis>
                          <Tooltip />
                          {c.legend !== false && <Legend wrapperStyle={{ fontSize: 11 }} />}
                          <Bar dataKey="value" fill="#22D3EE" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="bg-slate-100 border-t border-slate-300 px-2 py-1.5 flex items-center gap-1">
        {doc.sheets.map((s, i) => (
          <button key={i} data-testid={`sheet-tab-${i}`} onClick={() => setDoc({ ...doc, activeSheet: i })} onDoubleClick={() => renameSheet(i)} title="Double-click to rename"
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${i === active ? "bg-white text-slate-900 shadow-sm font-medium" : "text-slate-600 hover:bg-slate-200"}`}>
            {s.name}
          </button>
        ))}
        <button data-testid="sheet-add-tab" onClick={addSheet} title="Add worksheet" className="w-7 h-7 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-200"><Plus className="w-4 h-4" /></button>
        <span className="ml-auto text-[11px] text-slate-500 font-mono-data pr-1">{sel}: {formatValue(evalRef(cells, sel), (fmt[sel] || {}).numFmt) || "empty"}</span>
      </div>
    </div>
  );
}
